import { __assign, __awaiter, __generator } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { Layout, Modal } from 'antd';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import React, { Suspense, lazy, useCallback, useContext, useEffect, useState, } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, } from 'react-router-dom';
import packageJson from '../package.json';
import './App.less';
import { AppContext } from './auth';
import './auth/axios';
import componentLoader from './components/ComponentLoader/ComponentLoader';
import { config, jobTrackerApiPath } from './utils/apiPaths';
import { analytics, appCheck, auth, functions, logInWithEmailAndPassword, logout, perf, registerWithEmailAndPassword, signInWithGoogle, storage, triggerResetEmail, storageRef, } from './utils/firebase/firebaseIndex';
import { isPublicRoute } from './utils/publicRoutes';
import Loader from './components/Loader';
import MainLoader from './components/MainLoader';
import InitService, { APP, } from '@careerflow/common-components/dist/services/InitService';
import { initializeAnalytics, analyticsEvent, } from '@careerflow/common-components/dist/utils/Analytics';
import { useResumeSettingsStore, RESET_RESUME_SETTING, } from '@careerflow/common-components/dist/components/ResumeBuilder/store/resumeSettings';
import { useAiPopupStore } from '@careerflow/common-components/dist/components/ResumeBuilder/store/resumeAiPopup';
import useDocumentTitle from '@careerflow/common-components/dist/components/ResumeBuilder/CustomHooks/useDynamicTitle';
import en from './locales/en/translation.json';
import bg from './locales/bg/translation.json';
import cs from './locales/cs/translation.json';
import da from './locales/da/translation.json';
import de from './locales/de/translation.json';
import es from './locales/es/translation.json';
import et from './locales/et/translation.json';
import fi from './locales/fi/translation.json';
import fr from './locales/fr/translation.json';
import hi from './locales/hi/translation.json';
import hr from './locales/hr/translation.json';
import hu from './locales/hu/translation.json';
import it from './locales/it/translation.json';
import lt from './locales/lt/translation.json';
import lv from './locales/lv/translation.json';
import nl from './locales/nl/translation.json';
import no from './locales/no/translation.json';
import pl from './locales/pl/translation.json';
import pt from './locales/pt/translation.json';
import ro from './locales/ro/translation.json';
import sk from './locales/sk/translation.json';
import sl from './locales/sl/translation.json';
import sr from './locales/sr/translation.json';
import sv from './locales/sv/translation.json';
import zh from './locales/zh/translation.json';
import { initDB } from 'react-indexed-db-hook';
var DBConfig = {
    name: 'CareerflowDB',
    version: 1,
    objectStoresMeta: [
        {
            store: 'resume',
            storeConfig: { keyPath: 'name', autoIncrement: true },
            storeSchema: [
                { name: 'name', keypath: 'name', options: { unique: true } },
            ],
        },
        {
            store: 'resumeBuilder',
            storeConfig: { keyPath: 'id', autoIncrement: true },
            storeSchema: [{ name: 'id', keypath: 'id', options: { unique: true } }],
        },
    ],
};
try {
    initDB(DBConfig);
}
catch (err) {
    console.log('Error while initializing indexed DB');
}
var LinkedinHeadlineGenerator = lazy(function () {
    return componentLoader(function () { return import('./components/AITools/LinkedinHeadlineGenerator'); });
});
var LinkedinAboutGenerator = lazy(function () {
    return componentLoader(function () { return import('./components/AITools/LinkedinAboutGenerator'); });
});
var PostGenerator = lazy(function () {
    return componentLoader(function () { return import('./components/AITools/PostGenerator'); });
});
var ProfileCompletionCert = lazy(function () {
    return componentLoader(function () { return import('./components/ProfileCompletionCert'); });
});
var OnboardingQuestions = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/OnboardingQuestions'); });
});
var CoverLetter = lazy(function () {
    return componentLoader(function () { return import('./components/AITools/CoverLetter'); });
});
var CompanySize = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/CompanySize'); });
});
var ExpectedSalary = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/ExpectedSalary'); });
});
var Industries = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/Industries'); });
});
var JobSearchStatus = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/JobSearchStatus'); });
});
var LevelOfRoles = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/LevelOfRoles'); });
});
var QuesSecondPage = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/QuesSecondPage'); });
});
var RolesInterested = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/RolesInterested'); });
});
var WorkLocation = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/WorkLocation'); });
});
var Onboarding = lazy(function () {
    return componentLoader(function () { return import('./components/Signup/Onboarding'); });
});
var AuthScreen = lazy(function () {
    return componentLoader(function () { return import('./components/Signup/AuthScreen'); });
});
var Company = lazy(function () {
    return componentLoader(function () { return import('./components/Company/Company'); });
});
var JobTracker = lazy(function () {
    return componentLoader(function () { return import('./components/JobTracker/JobTracker'); });
});
var ProfilePage = lazy(function () {
    return componentLoader(function () { return import('./components/ProfilePage/ProfilePage'); });
});
var Setting = lazy(function () {
    return componentLoader(function () { return import('./components/Setting/Setting'); });
});
var ContactList = lazy(function () {
    return componentLoader(function () { return import('./components/Contacts/ContactList'); });
});
var ExtensionLogin = lazy(function () {
    return componentLoader(function () { return import('./components/ExtensionLogin/ExtensionLogin'); });
});
var StudentGoals = lazy(function () {
    return componentLoader(function () { return import('./components/Goals/StudentGoals'); });
});
var StudentGoalDetails = lazy(function () {
    return componentLoader(function () { return import('./components/Goals/StudentGoalsDetails'); });
});
var Jobs = lazy(function () { return componentLoader(function () { return import('./components/Jobs'); }); });
var Linkedin = lazy(function () {
    return componentLoader(function () { return import('./components/Linkedin'); });
});
var Logout = lazy(function () {
    return componentLoader(function () { return import('./components/Logout/Logout'); });
});
var Navbar = lazy(function () {
    return componentLoader(function () { return import('./components/Navbar/Navbar'); });
});
var NewOnboarding = lazy(function () {
    return componentLoader(function () { return import('./components/NewOnboarding'); });
});
var Features = lazy(function () {
    return componentLoader(function () { return import('./components/NewOnboarding/Features'); });
});
var SelectPlan = lazy(function () {
    return componentLoader(function () { return import('./components/NewOnboarding/Plans'); });
});
var FirstPageInfo = lazy(function () {
    return componentLoader(function () { return import('./components/OnboardingInfo/FirstPageInfo'); });
});
var LastPageInfo = lazy(function () {
    return componentLoader(function () { return import('./components/OnboardingInfo/LastPageInfo'); });
});
var PaymentPlans = lazy(function () {
    return componentLoader(function () { return import('./components/PaymentsPlan'); });
});
var QuesFirstPage = lazy(function () {
    return componentLoader(function () { return import('./components/Questionnaire/QuesFirstPage'); });
});
var Resources = lazy(function () {
    return componentLoader(function () { return import('./components/Resources/Resources'); });
});
var SideNav = lazy(function () {
    return componentLoader(function () { return import('./components/SideNav/SideNav'); });
});
var ThanksUpgrade = lazy(function () {
    return componentLoader(function () { return import('./components/ThanksUpgrade/ThanksUpgrade'); });
});
var Dashboard = lazy(function () {
    return componentLoader(function () { return import('./pages/Dashboard'); });
});
var AcceptInviteBanner = lazy(function () {
    return componentLoader(function () { return import('./components/AcceptInviteBanner'); });
});
var AIMockInterview = lazy(function () {
    return componentLoader(function () { return import('./pages/AIMockInterview'); });
});
var ElevatorPitch = lazy(function () {
    return componentLoader(function () { return import('./components/AITools/ElevatorPitch'); });
});
var InstituteOverview = lazy(function () {
    return componentLoader(function () { return import('./components/InstituteOverview/InstituteOverview'); });
});
var ResumeBuilderList = lazy(function () {
    return componentLoader(function () { return import('./pages/ResumeBuilderList'); });
});
var ResumeBuilder = lazy(function () {
    return componentLoader(function () { return import('./pages/ResumeBuilder'); });
});
var Documents = lazy(function () {
    return componentLoader(function () { return import('./pages/Documents'); });
});
function App() {
    var _this = this;
    var _a;
    var _b = useContext(AppContext), userInfo = _b.userInfo, loading = _b.loading, isMobile = _b.isMobile, setUserInfo = _b.setUserInfo, upgrade = _b.upgrade, setUpgrade = _b.setUpgrade, setIsExtensionInstalled = _b.setIsExtensionInstalled, user = _b.user;
    var Header = Layout.Header, Content = Layout.Content, Sider = Layout.Sider;
    var _c = useState(false), showFeatures = _c[0], setShowFeatures = _c[1];
    var _d = useState(false), showEmailVerificationModal = _d[0], setShowEmailVerificationModal = _d[1];
    var _e = useState(false), paymentPageVisited = _e[0], setPaymentPageVisited = _e[1];
    var exponentialRef = React.useRef(500);
    var _f = useState(false), featureMode = _f[0], setFeatureMode = _f[1];
    var _g = useState(isMobile), isMenuFolded = _g[0], setIsMenuFolded = _g[1];
    var _h = useState(false), boardLoading = _h[0], setBoardLoading = _h[1];
    var _j = React.useState(), board = _j[0], setBoard = _j[1];
    var _k = useState([]), sectionList = _k[0], setSectionList = _k[1];
    var _l = useState([]), deletedJobList = _l[0], setDeletedJobList = _l[1];
    var _m = useState(''), documentTitle = _m[0], setDocumentTitle = _m[1];
    var isInstituteCoach = (_a = Object.keys(userInfo.instituteRoles || {})) === null || _a === void 0 ? void 0 : _a[0];
    var location = useLocation();
    var navigate = useNavigate();
    var _o = useState(), instituteId = _o[0], setinstituteId = _o[1];
    var setResumeSettings = useResumeSettingsStore(function (state) { return state.setResumeSettings; });
    var setAiPopupVisible = useAiPopupStore().setAiPopupVisible;
    useDocumentTitle(documentTitle);
    useEffect(function () {
        console.log(location.pathname, 'pathname');
        if (location.pathname.includes('/resumebuilder')) {
            setDocumentTitle('Resume Builder by Careerflow');
        }
        else if (location.pathname.includes('/dashboard')) {
            setDocumentTitle('Welcome to Careerflow Dashboard');
        }
        else if (location.pathname.includes('/board')) {
            setDocumentTitle('Job Tracker | Track and manage job applications');
        }
        else if (location.pathname.includes('/contact')) {
            setDocumentTitle('Contact and Networking | Save contacts, schedule followups and more');
        }
        else if (location.pathname.includes('/documents')) {
            setDocumentTitle('Document Manager | Add, View, Download and organize documents');
        }
        else if (location.pathname.includes('/cover-letter')) {
            setDocumentTitle('AI Cover letters personalized for job and your profile by Careerflow');
        }
        else if (location.pathname.includes('/post-generator')) {
            setDocumentTitle('Generate social media or LinkedIn Posts powered by Careerflow AI');
        }
        else if (location.pathname.includes('/linkedin-headline')) {
            setDocumentTitle('LinkedIn Headline Generator | Powered by Careerflow AI');
        }
        else if (location.pathname.includes('/linkedin-about')) {
            setDocumentTitle('LinkedIn Profile About Section Generator | Powered by Careerflow AI');
        }
        else if (location.pathname.includes('/linkedin')) {
            setDocumentTitle('LinkedIn Optimization by Careerflow | View Suggestions and improve your LinkedIn profile');
        }
        else if (location.pathname.includes('/elevator-pitch')) {
            setDocumentTitle('Elevator Pitch By Careerflow AI');
        }
        else {
            setDocumentTitle('Careerflow Webapp | AI powered Job Applications, Resume Builder, Cover letter, LinkedIn optimization and more');
        }
    }, [location]);
    React.useEffect(function () {
        if (!user) {
            return;
        }
        //@ts-expect-error @ts-ignore
        window.Intercom('boot', {
            api_base: 'https://api-iam.intercom.io',
            app_id: 'anr89zpe',
            name: user.displayName,
            email: user.email,
            created_at: user.metadata.createdAt.slice(0, -3),
            user_id: user.uid,
        });
    }, [user]);
    React.useEffect(function () {
        if (process.env.REACT_APP_SENTRY_DSN)
            Sentry.init({
                dsn: process.env.REACT_APP_SENTRY_DSN,
                integrations: [new BrowserTracing()],
                release: packageJson.version,
                tracesSampleRate: 0,
                sampleRate: 0.75,
                environment: process.env.REACT_APP_ENVIRONMENT,
            });
        if (userInfo.email) {
            Sentry.setUser({
                email: userInfo.email,
            });
        }
    }, [userInfo.email]);
    React.useEffect(function () {
        if (upgrade) {
            analyticsEvent('Open Upgrade to premium modal');
        }
    }, [upgrade]);
    useEffect(function () {
        var _a, _b;
        var instituteid = null;
        if (isInstituteCoach) {
            instituteid = isInstituteCoach;
            setinstituteId(instituteid);
        }
        else if ((userInfo === null || userInfo === void 0 ? void 0 : userInfo.invites) &&
            ((_a = Object.keys(userInfo === null || userInfo === void 0 ? void 0 : userInfo.invites)) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            instituteid = (_b = Object.keys(userInfo === null || userInfo === void 0 ? void 0 : userInfo.invites)) === null || _b === void 0 ? void 0 : _b[0];
            setinstituteId(instituteid);
        }
    }, [userInfo === null || userInfo === void 0 ? void 0 : userInfo.invites, isInstituteCoach]);
    React.useEffect(function () {
        if (!isMobile && isMenuFolded) {
            setIsMenuFolded(false);
        }
    }, [isMenuFolded, isMobile]);
    React.useEffect(function () {
        if (!(user === null || user === void 0 ? void 0 : user.emailVerified) && userInfo.created > 1694802600000) {
            setShowEmailVerificationModal(true);
        }
        initializeAnalytics(userInfo.id, {
            userType: userInfo.userType || 'basic',
            signupSource: localStorage.getItem('signupSource'),
            email: userInfo.email,
        }, {
            amplitudeKey: process.env.REACT_APP_AMPLITUDE_KEY,
            gaKey: process.env.REACT_APP_ANALYTICS_MEASUREMENT_ID,
        });
        analyticsEvent('initialize');
    }, [
        userInfo.id,
        userInfo.signupSource,
        userInfo.userType,
        userInfo.created,
        userInfo.email,
        user === null || user === void 0 ? void 0 : user.emailVerified,
    ]);
    React.useEffect(function () {
        var _a, _b, _c, _d;
        if (window.location.pathname === '/logout') {
            localStorage.setItem('logout', 'true');
        }
        (_b = (_a = window.chrome) === null || _a === void 0 ? void 0 : _a.runtime) === null || _b === void 0 ? void 0 : _b.sendMessage(process.env.REACT_APP_EXTENSION_ID, { ping: true }, function (res) {
            if (res)
                setIsExtensionInstalled(true);
        });
        if (window.location.pathname === '/uninstall-redirect') {
            setTimeout(function () {
                analyticsEvent('Uninstall Extension');
                window.open('https://docs.google.com/forms/d/1WLsY2WRC3SfJTtFZGbdTzlxR1erSiQQuSSk5A6gRUeA', '_self');
            }, 1000);
        }
        (_d = (_c = window.chrome) === null || _c === void 0 ? void 0 : _c.runtime) === null || _d === void 0 ? void 0 : _d.sendMessage(process.env.REACT_APP_EXTENSION_ID, { ping: true }, function (res) {
            if (res)
                setIsExtensionInstalled(true);
        });
        if (localStorage.getItem('logout'))
            logout();
        var queryParams = new URLSearchParams(window.location.search);
        var invitedBy = queryParams.get('invite');
        var referal = queryParams.get('referalId');
        var priceId = queryParams.get('priceId');
        var coupon = queryParams.get('coupon');
        if (invitedBy) {
            localStorage.setItem('invitedBy', invitedBy);
        }
        if (referal) {
            localStorage.setItem('referalId', referal);
        }
        if (priceId) {
            localStorage.setItem('priceId', priceId);
        }
        if (coupon) {
            localStorage.setItem('coupon', coupon);
        }
    }, [setIsExtensionInstalled]);
    var getBoard = React.useCallback(function (callback) {
        setBoardLoading(true);
        axios
            .get("".concat(jobTrackerApiPath, "/board"))
            .then(function (res) {
            var _a;
            setBoard(res.data);
            setSectionList(res.data.sectionList);
            setDeletedJobList(((_a = res.data) === null || _a === void 0 ? void 0 : _a.deletedJobCardList) || []);
            if (callback)
                callback(res.data);
        })
            .finally(function () { return setBoardLoading(false); });
    }, []);
    var forceTokenRefresh = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var idToken, decodedToken_1, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!user) return [3 /*break*/, 4];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, user.getIdToken(true)];
                case 2:
                    idToken = _a.sent();
                    decodedToken_1 = jwt_decode(idToken);
                    if (userInfo.userType !== 'premium' &&
                        decodedToken_1.userType === 'premium') {
                        exponentialRef.current = 1200001;
                        setUserInfo(function (userInfo) { return (__assign(__assign({}, userInfo), { userType: decodedToken_1.userType || 'basic' })); });
                    }
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    console.log('Error refreshing token:', error_1);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [setUserInfo, user, userInfo.userType]);
    useEffect(function () {
        var _a, _b;
        if (((userInfo === null || userInfo === void 0 ? void 0 : userInfo.userType) === 'premium' || (userInfo === null || userInfo === void 0 ? void 0 : userInfo.premiumUser)) &&
            ((_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.pathname) === null || _b === void 0 ? void 0 : _b.includes('/plan'))) {
            navigate('/dashboard');
            setUpgrade(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);
    var handleClearResumeSettingsStore = function () {
        setResumeSettings(RESET_RESUME_SETTING);
        setAiPopupVisible(false);
    };
    var institutePath = instituteId ? encodeURI(instituteId) : '';
    var tryRefreshToken = useCallback(function () {
        setTimeout(function () {
            if (exponentialRef.current > 1200000)
                return;
            exponentialRef.current = exponentialRef.current + 1000;
            forceTokenRefresh();
        }, exponentialRef.current);
    }, [forceTokenRefresh]);
    React.useEffect(function () {
        if (paymentPageVisited) {
            tryRefreshToken();
        }
    }, [paymentPageVisited, tryRefreshToken]);
    React.useEffect(function () {
        // TODO add logic to check the timestamp of jobSaved and only call getBoard on tab focus
        setInterval(function () {
            var jobSavedTimestamp = localStorage.getItem('jobSaved');
            if (!jobSavedTimestamp)
                return;
            var difference = Math.abs(Date.now() - Number.parseInt(jobSavedTimestamp)) / 1000;
            if (difference > 2) {
                // assuming it would take 2 seconds to save the job
                getBoard();
                localStorage.removeItem('jobSaved');
            }
        }, 4000);
    }, [getBoard]);
    useEffect(function () {
        InitService.init(config.baseUrl || '', AppContext, {
            analytics: analytics,
            appCheck: appCheck,
            auth: auth,
            functions: functions,
            logInWithEmailAndPassword: logInWithEmailAndPassword,
            logout: logout,
            perf: perf,
            registerWithEmailAndPassword: registerWithEmailAndPassword,
            signInWithGoogle: signInWithGoogle,
            storage: storage,
            triggerResetEmail: triggerResetEmail,
            storageRef: storageRef,
        }, APP.CF_WEBAPP, undefined, {
            en: en,
            bg: bg,
            cs: cs,
            da: da,
            de: de,
            es: es,
            et: et,
            fi: fi,
            fr: fr,
            hi: hi,
            hr: hr,
            hu: hu,
            it: it,
            lt: lt,
            lv: lv,
            nl: nl,
            no: no,
            pl: pl,
            pt: pt,
            ro: ro,
            sk: sk,
            sl: sl,
            sr: sr,
            sv: sv,
            zh: zh,
        });
    }, []);
    if (loading)
        return _jsx(MainLoader, {});
    else
        return (_jsx("div", __assign({ className: "App" }, { children: _jsxs(Suspense, __assign({ fallback: _jsx(MainLoader, {}) }, { children: [_jsx(Features, { open: showFeatures, setOpen: setShowFeatures, featureMode: featureMode, setFeatureMode: setFeatureMode }), _jsx(Modal, __assign({ style: { top: '2rem' }, width: window.innerWidth > 1200 ? '70%' : '90%', bodyStyle: {
                            padding: 0,
                            borderRadius: '2rem',
                        }, maskTransitionName: "", maskStyle: { borderRadius: '2rem' }, onCancel: function () {
                            analyticsEvent('Close payments modal');
                            setUpgrade(false);
                            if (paymentPageVisited)
                                tryRefreshToken();
                        }, footer: false, open: upgrade }, { children: _jsx(PaymentPlans, { setPaymentPageVisited: setPaymentPageVisited, setUpgrade: setUpgrade, setShowFeatures: setShowFeatures }) })), !loading && Object.keys(userInfo || {}).length ? (_jsx(_Fragment, { children: 
                        // User logged in and verified (authorized)
                        user &&
                            ((userInfo === null || userInfo === void 0 ? void 0 : userInfo.initialOnboardingDone) ||
                                (userInfo === null || userInfo === void 0 ? void 0 : userInfo.created) < 1703010600000) &&
                            (user.emailVerified || (userInfo === null || userInfo === void 0 ? void 0 : userInfo.created) < 1694802600000) &&
                            !isPublicRoute() ? (_jsxs("div", __assign({ style: { display: 'flex', flexDirection: 'column' } }, { children: [!window.location.pathname.includes('resumebuilder/editor') ? (_jsx(Header, __assign({ style: { zIndex: '1000' } }, { children: _jsx(Navbar, { isMenuUnfolded: isMenuFolded, setIsMenuUnfolded: setIsMenuFolded, setUpgrade: setUpgrade }) }))) : null, !isMenuFolded && (_jsx(Sider, __assign({ hidden: window.location.pathname.includes('/resumebuilder/editor'), style: {
                                        height: 'calc(100vh - 50px)',
                                        top: '50px',
                                        position: 'fixed',
                                        zIndex: 2,
                                    }, width: 210, className: "site-layout-background" }, { children: _jsx(SideNav, {}) }))), _jsx("div", { children: _jsxs(Content, __assign({ className: "site-layout-background", style: {
                                            margin: 0,
                                            minHeight: '90vh',
                                            backgroundColor: '#f8fbfe',
                                            position: 'absolute',
                                            left: isMenuFolded ||
                                                window.location.pathname.includes('/resumebuilder/editor')
                                                ? '0'
                                                : '210px',
                                            width: !window.location.pathname.includes('resumebuilder/editor')
                                                ? 'calc(100% - 210px)'
                                                : '100%',
                                        } }, { children: [_jsx(AcceptInviteBanner, {}), _jsx(Suspense, __assign({ fallback: _jsx(Loader, {}) }, { children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(Navigate, { to: "/dashboard", replace: true }) }), _jsx(Route, { path: "/extension/login", element: _jsx(ExtensionLogin, { showEmailVerificationModal: showEmailVerificationModal, setShowEmailVerificationModal: setShowEmailVerificationModal }) }), _jsx(Route, { path: "/board", element: _jsx(JobTracker, { board: board, boardLoading: boardLoading, sectionList: sectionList, setSectionList: setSectionList, deletedJobList: deletedJobList, getBoard: getBoard }) }), _jsx(Route, { path: "/mock-interview", element: _jsx(AIMockInterview, {}) }), _jsx(Route, { path: "/elevator-pitch", element: _jsx(ElevatorPitch, {}) }), _jsx(Route, { path: "/dashboard", element: _jsx(Dashboard, { setFeatureMode: setFeatureMode, setOpenFeatures: setShowFeatures }) }), _jsx(Route, { path: "/resumebuilder", element: _jsx(ResumeBuilderList, { handleClearResumeSettingsStore: handleClearResumeSettingsStore }) }), _jsx(Route, { path: "/resumebuilder/editor", element: _jsx(ResumeBuilder, { handleClearResumeSettingsStore: handleClearResumeSettingsStore }) }), _jsx(Route, { path: "/cover-letter", element: _jsx(CoverLetter, { upgrade: upgrade, setUpgrade: setUpgrade }) }), _jsx(Route, { path: "/post-generator", element: _jsx(PostGenerator, { upgrade: upgrade, setUpgrade: setUpgrade }) }), _jsx(Route, { path: "/linkedin-about", element: _jsx(LinkedinAboutGenerator, { upgrade: upgrade, setUpgrade: setUpgrade }) }), _jsx(Route, { path: "/linkedin-headline", element: _jsx(LinkedinHeadlineGenerator, { upgrade: upgrade, setUpgrade: setUpgrade }) }), _jsx(Route, { path: "/company", element: _jsx(Company, {}) }), _jsx(Route, { path: "/profile", element: _jsx(ProfilePage, {}) }), _jsx(Route, { path: "/settings", element: _jsx(Setting, {}) }), _jsx(Route, { path: "/contact", element: _jsx(ContactList, {}) }), _jsx(Route, { path: "/documents", element: _jsx(Documents, {}) }), _jsx(Route, { path: "/linkedin", element: _jsx(Linkedin, {}) }), _jsx(Route, { path: "/".concat(institutePath, "-goals"), element: _jsx(StudentGoals, {}) }), _jsx(Route, { path: "/".concat(institutePath, "-goals/:id"), element: _jsx(StudentGoalDetails, {}) }), _jsx(Route, { path: "/".concat(institutePath, "-jobs"), element: _jsx(Jobs, { board: board, sectionList: sectionList, getBoard: getBoard }) }), _jsx(Route, { path: "/".concat(institutePath, "-overview"), element: _jsx(InstituteOverview, {}) }), _jsx(Route, { path: "/".concat(institutePath, "-resources"), element: _jsx(Resources, {}) }), _jsx(Route, { path: "/new-onboarding", element: _jsx(NewOnboarding, {}) }), _jsx(Route, { path: "*", element: _jsx(Navigate, { to: "/dashboard", replace: true }) })] }) }))] })) })] }))) : (_jsx(Suspense, __assign({ fallback: _jsx(MainLoader, {}) }, { children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(Navigate, { to: "/dashboard", replace: true }) }), _jsx(Route, { path: "/certificate/:certificateId", element: _jsx(ProfileCompletionCert, {}) }), _jsx(Route, { path: "/extension/login", element: _jsx(ExtensionLogin, { showEmailVerificationModal: showEmailVerificationModal, setShowEmailVerificationModal: setShowEmailVerificationModal }) }), _jsx(Route, { path: "/login", element: _jsx(AuthScreen, { showEmailVerificationModal: showEmailVerificationModal, setShowEmailVerificationModal: setShowEmailVerificationModal }) }), _jsx(Route, { path: "/logout", element: _jsx(Logout, {}) }), _jsx(Route, { path: "/walkthrough", element: _jsx(NewOnboarding, {}) }), _jsx(Route, { path: "/plan", element: _jsx(SelectPlan, { setUpgrade: setUpgrade, setShowFeatures: setShowFeatures }) }), _jsx(Route, { path: "/thanks", element: _jsx(ThanksUpgrade, {}) }), _jsxs(Route, __assign({ path: "onboarding-questions", element: _jsx(OnboardingQuestions, {}) }, { children: [_jsx(Route, { path: "onboarding-start", element: _jsx(FirstPageInfo, {}) }), _jsx(Route, { path: "0", element: _jsx(Onboarding, {}) }), _jsx(Route, { path: "1", element: _jsx(QuesSecondPage, {}) }), _jsx(Route, { path: "2", element: _jsx(QuesFirstPage, {}) }), _jsx(Route, { path: "3", element: _jsx(RolesInterested, {}) }), _jsx(Route, { path: "4", element: _jsx(WorkLocation, {}) }), _jsx(Route, { path: "5", element: _jsx(LevelOfRoles, {}) }), _jsx(Route, { path: "6", element: _jsx(CompanySize, {}) }), _jsx(Route, { path: "7", element: _jsx(Industries, {}) }), _jsx(Route, { path: "8", element: _jsx(ExpectedSalary, {}) }), _jsx(Route, { path: "9", element: _jsx(JobSearchStatus, {}) }), _jsx(Route, { path: "onboarding-end", element: _jsx(LastPageInfo, {}) })] }))] }) }))) })) : (_jsx(MainLoader, {}))] })) })));
}
export default App;
